import { useCallback, useMemo } from "react";

import { getIdFromAccessId } from "../shared/access-id";

import { useJoinAsSectionAssistant } from "./hooks/use-join-as-section-assistant";
import { usePageActions } from "./page";
import { useQueryParamsManager } from "./routing/hooks/use-query-params-manager";
import { getNavigationValues, pageUrl } from "./urls";

import { AssessmentStatus } from "api/api-data-types";
import { serverReleaseAssessments } from "api/api-server-actions";
import { serverGetPageAssessmentStatuses } from "api/api-server-data";
import { OutcomeScoreTableData, SectionScoreTableRow } from "api/api-types-assessment";
import {
  ACCESS_ID_QUERY_PARAM,
  ASSESSMENT_VIEW_QUERY_PARAM,
  SECONDARY_OVERLAY_QUERY_PARAM,
  SUBMISSION_ID_QUERY_PARAM,
  SecondaryOverlay,
} from "components/constants";
import {
  LAST_ASSESSMENT_PANEL_ACCESS_ID,
  getTotalSubmissions,
} from "components/materials/page/assessment/helpers";
import { ResponseType, useResponsesForPage } from "components/materials/page/responses";
import { showStopPresentingForAssessmentModal } from "components/materials/presentation/StopPresentingForAssessmentModal";
import { OrgType, PageType, RubricType, SectionScoreDisplayFormat } from "components/server-types";
import { t } from "i18n/i18n";
import { ORDER_BY_CREATED_DATE_DESC, useCustomOrdered } from "mds/hooks/use-ordered";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  sectionUserSelectors,
  selectCurrentPageId,
  selectCurrentSectionIsArchived,
  selectDefaultSubmissionForUser,
  selectIsUnassessablePage,
  selectOfficialFullSubmissionsByPageId,
  selectSubmissionsByPageId,
} from "store/selectors";
import { selectPageGroupUsersByPageId } from "store/selectors/page-groups";
import { isIndividualSubmission } from "store/selectors/type-helpers";
import { computedStateActions } from "store/slices/computed";
import { localStateActions } from "store/slices/local";
import { useListSelector } from "store/store-hooks";
import { toastSuccessMessage } from "utils/alerts";
import { stopPresenting, usePresentation } from "utils/presentation";

export const getMaxSectionScoreForOrg = (org: OrgType): number => {
  return {
    [SectionScoreDisplayFormat.Percent]: 100,
    [SectionScoreDisplayFormat.Scale_4]: 4,
    [SectionScoreDisplayFormat.Scale_5]: 5,
  }[org.section_score_display_format];
};

const roundHalfUpToOneDecimal = (value: number): number => Math.round(value * 10) / 10;

/** Convert a 0–100 percent to a rubric score via linear interpolation of conversion_percent. */
export const getRubricScoreFromPercent = (percent: number, rubric: RubricType): number | null => {
  if (percent < 0 || rubric.length < 2) return null;

  const clampedPercent = Math.min(percent, 100);

  for (let i = 0; i < rubric.length - 1; i += 1) {
    const lower = rubric[i];
    const upper = rubric[i + 1];

    if (lower.conversion_percent <= clampedPercent && clampedPercent <= upper.conversion_percent) {
      const span = upper.conversion_percent - lower.conversion_percent;
      if (span === 0) return roundHalfUpToOneDecimal(lower.score);

      const fraction = (clampedPercent - lower.conversion_percent) / span;
      return roundHalfUpToOneDecimal(lower.score + fraction * (upper.score - lower.score));
    }
  }

  return null;
};

export const useAssessmentActions = () => {
  type AssessmentCreateOrUpdateType = {
    points: number | null | undefined;
    comment: string;
    page_outcomes: Record<string, number>;
    is_official: boolean;
  };
  const createAssessment = (
    params: AssessmentCreateOrUpdateType,
    assessedById: string,
    submissionId: string,
    sectionId: string,
  ) => {
    return storeApi.assessments.create({
      points: params.points,
      comment: params.comment,
      page_outcomes: params.page_outcomes,
      assessed_by_id: assessedById,
      section_id: sectionId,
      submission_id: submissionId,
      is_official: params.is_official,
    });
  };
  const updateAssessment = (
    assessmentId: string,
    params: AssessmentCreateOrUpdateType,
    assessedById: string,
    submissionId: string,
  ) => {
    return storeApi.assessments.partial_update({
      id: assessmentId,
      points: params.points,
      comment: params.comment,
      page_outcomes: params.page_outcomes,
      assessed_by_id: assessedById,
      submission_id: submissionId,
      is_official: params.is_official,
    });
  };

  const deleteAssessment = (assessmentId: string) => {
    return storeApi.assessments.destroy(assessmentId).then(() => {
      toastSuccessMessage(t("success.toasts.delete_success", { item: t(`glossary.assessment`) }));
    });
  };

  return { createAssessment, updateAssessment, deleteAssessment };
};

export const useAssessmentMode = (page: PageType | null) => {
  const dispatch = useAppDispatch();
  const { navigateToPage } = usePageActions();
  const { setQueryParams } = useQueryParamsManager();

  const sectionId = page?.section_id;

  const { isPresenting, presentedSessionId } = usePresentation();
  const pageId = useAppSelector(selectCurrentPageId);
  const isArchived = useAppSelector(selectCurrentSectionIsArchived);
  const isUnassessable = useAppSelector((s) => selectIsUnassessablePage(s, page));
  const { joinAsSectionAssistantIfNew } = useJoinAsSectionAssistant();

  const startAssessing = useCallback(async () => {
    // Archived sections are read-only and cannot enter assessment mode.
    if (!page || isArchived || isUnassessable) return;

    if (isPresenting && presentedSessionId) {
      const shouldContinue = await showStopPresentingForAssessmentModal();
      if (!shouldContinue) {
        return;
      }
      await stopPresenting(presentedSessionId);
    }

    await joinAsSectionAssistantIfNew();

    dispatch(localStateActions.setIsEditMode(false));
    setQueryParams({ [SECONDARY_OVERLAY_QUERY_PARAM]: null });

    if (pageId !== page.id) {
      const href = pageUrl(sectionId!, page.id, {
        additionalParams: `${ASSESSMENT_VIEW_QUERY_PARAM}=1`,
      });
      navigateToPage(page, href);
    } else {
      setQueryParams({
        [ASSESSMENT_VIEW_QUERY_PARAM]: "1",
        [SECONDARY_OVERLAY_QUERY_PARAM]: null,
        [SUBMISSION_ID_QUERY_PARAM]: null,
      });
    }
  }, [
    isPresenting,
    presentedSessionId,
    dispatch,
    page,
    pageId,
    sectionId,
    isArchived,
    isUnassessable,
    navigateToPage,
    setQueryParams,
    joinAsSectionAssistantIfNew,
  ]);

  const stopAssessing = useCallback(
    async (additionalParams?: Record<string, string | null>) => {
      if (!page?.id) return;
      const statuses = await serverGetPageAssessmentStatuses({ pageIds: [page.id] });
      dispatch(computedStateActions.savePageAssessmentStatuses(statuses));

      setQueryParams({
        [ACCESS_ID_QUERY_PARAM]: null,
        [ASSESSMENT_VIEW_QUERY_PARAM]: null,
        [SUBMISSION_ID_QUERY_PARAM]: null,
        ...additionalParams,
      });
    },
    [setQueryParams, dispatch, page?.id],
  );

  const stopAssessingAndMaybeShowAssessmentSummary = () => {
    stopAssessing(
      page?.assessments_published_at
        ? { [SECONDARY_OVERLAY_QUERY_PARAM]: SecondaryOverlay.AssessmentSummary }
        : undefined,
    );
  };

  return {
    startAssessing,
    stopAssessing,
    stopAssessingAndMaybeShowAssessmentSummary,
  };
};

export const useAssessmentPublishActions = (page: PageType | null) => {
  const dispatch = useAppDispatch();
  const { setQueryParams } = useQueryParamsManager();
  const publishGrades = async () => {
    if (!page) return;
    await serverReleaseAssessments(page.id);
    toastSuccessMessage(t("success.toasts.publish_assessments_success"));
  };

  const unpublishGrades = () => {
    if (!page) return;
    storeApi.pages.partial_update({ id: page.id, assessments_published_at: null });
    dispatch(
      computedStateActions.savePageAssessmentStatuses({
        [page.id]: {
          status: AssessmentStatus.ASSESSABLE,
          unassessed_late_submissions_count: 0,
          assessed_submissions_count: 0,
          unseen_late_submissions_count: 0,
          unseen_submissions_count: 0,
        },
      }),
    );
    setQueryParams({ [SECONDARY_OVERLAY_QUERY_PARAM]: null });
  };

  return { publishGrades, unpublishGrades };
};

export type AssessmentCountData = {
  count: number;
  total: number;
};

export type AssessmentCountDataWithNotSubmitted = AssessmentCountData & {
  notSubmittedCount: number;
};

/**
 * Hook that returns count and total without calculating notSubmittedCount.
 * Use this when you don't need notSubmittedCount.
 */
export const useAssessedCountData = (page: PageType | null): AssessmentCountData => {
  const pageId = page?.id ?? null;
  // Submissions must be already loaded by the parent components before using this hook.
  const submissions = useListSelector((s) => selectSubmissionsByPageId(s, pageId));
  const officialSubmissions = useListSelector((s) =>
    selectOfficialFullSubmissionsByPageId(s, pageId),
  );
  const enabledPageGroupUsers = useListSelector((s) => selectPageGroupUsersByPageId(s, pageId));

  if (!page) return { count: 0, total: 0 };

  const total = getTotalSubmissions(page, submissions, enabledPageGroupUsers);
  const count = officialSubmissions.length;

  return { count, total };
};

/**
 * Hook that returns count, total, and notSubmittedCount.
 * Use this when you need notSubmittedCount, but be aware it causes re-renders when a new user types in the doc because
 *  of useWorksheetAccessIds.
 */
export const useAssessedCountDataWithNotSubmitted = (
  page: PageType | null,
): AssessmentCountDataWithNotSubmitted => {
  const { count, total } = useAssessedCountData(page);
  const { responses } = useResponsesForPage(page);
  const notSubmittedCount = responses.filter((r) => r.id).length - total;
  return { count, total, notSubmittedCount };
};

export const useFirstAssessableResponseId = (page: PageType | null, responses: ResponseType[]) => {
  const submissions = useCustomOrdered(
    useAppSelector((s) => selectSubmissionsByPageId(s, page?.id ?? "")),
    ORDER_BY_CREATED_DATE_DESC,
  );
  const sectionUserMap = useAppSelector(sectionUserSelectors.selectEntities);

  if (!page) return undefined;

  for (const response of responses) {
    const actualId = getIdFromAccessId(response.id);

    const lastSubmission = submissions.find((submission) => {
      if (isIndividualSubmission(submission)) {
        const sectionUser = sectionUserMap[submission.section_user_id];
        return sectionUser?.user_id === actualId;
      }
      return submission.page_group_id === actualId;
    });

    if (lastSubmission && !lastSubmission.official_at) {
      return response.id;
    }
  }
};

export const useAssessmentNavData = (page: PageType | null) => {
  const { getQueryParam, setQueryParams } = useQueryParamsManager();
  const { responses } = useResponsesForPage(page);

  const panels = useMemo(
    () => responses.map((response) => response.id).concat([LAST_ASSESSMENT_PANEL_ACCESS_ID]),
    [responses],
  );

  const firstId = useFirstAssessableResponseId(page, responses);
  const accessParamId = getQueryParam(ACCESS_ID_QUERY_PARAM);

  const currentId = accessParamId || firstId;
  const isLastPanel = currentId === LAST_ASSESSMENT_PANEL_ACCESS_ID;

  const { nextId, prevId, nextURL, prevURL } = useMemo(
    () =>
      getNavigationValues(panels, {
        queryParam: ACCESS_ID_QUERY_PARAM,
        currentId,
      }),
    [panels, currentId],
  );

  const submissionForNextUser = useAppSelector((s) =>
    selectDefaultSubmissionForUser(s, page?.id ?? "", nextId, true),
  );

  const submissionForPrevUser = useAppSelector((s) =>
    selectDefaultSubmissionForUser(s, page?.id ?? "", prevId, true),
  );

  const memoizedNextURL = useMemo(() => {
    if (!nextURL) return null;

    const url = new URL(nextURL.toString());

    if (submissionForNextUser) {
      url.searchParams.set(SUBMISSION_ID_QUERY_PARAM, submissionForNextUser.id);
    }

    url.searchParams.set(ASSESSMENT_VIEW_QUERY_PARAM, "1");
    return url;
  }, [nextURL, submissionForNextUser]);

  const memoizedPrevURL = useMemo(() => {
    if (!prevURL) return null;

    const url = new URL(prevURL.toString());

    if (submissionForPrevUser) {
      url.searchParams.set(SUBMISSION_ID_QUERY_PARAM, submissionForPrevUser.id);
    }

    url.searchParams.set(ASSESSMENT_VIEW_QUERY_PARAM, "1");
    return url;
  }, [prevURL, submissionForPrevUser]);

  const onBack = useCallback(
    (e?: React.MouseEvent<HTMLButtonElement>) => {
      e?.preventDefault();
      e?.stopPropagation();
      setQueryParams({
        [ACCESS_ID_QUERY_PARAM]: prevId || null,
        [SUBMISSION_ID_QUERY_PARAM]: submissionForPrevUser?.id || null,
      });
    },
    [setQueryParams, prevId, submissionForPrevUser],
  );

  const onNext = useCallback(() => {
    setQueryParams({
      [ACCESS_ID_QUERY_PARAM]: nextId || null,
      [SUBMISSION_ID_QUERY_PARAM]: submissionForNextUser?.id || null,
    });
  }, [setQueryParams, nextId, submissionForNextUser]);

  return {
    onNext,
    onBack,
    nextURL: memoizedNextURL,
    prevURL: memoizedPrevURL,
    prevId,
    nextId,
    currentId,
    isLastPanel,
    submissionForNextUser,
    submissionForPrevUser,
  };
};

/**
 * Helper method for components that need to render an empty state when section
 * score data is unavailable. The API endpoint always produces some response data
 * even if no assessments have been done, so we have to scan through each row to
 * check for null values.
 */
export const isSectionScoreDataSufficient = (
  sectionScoreTableRows: SectionScoreTableRow[],
): boolean => {
  return sectionScoreTableRows.some(
    (row) =>
      row.prep_work_score !== null ||
      row.activities_score !== null ||
      row.assignments_score !== null,
  );
};

export const isOutcomeScoreTableDataSufficient = (
  outcomeScoreTableData: OutcomeScoreTableData,
): boolean => {
  return (
    outcomeScoreTableData.headers.length > 0 &&
    outcomeScoreTableData.rows.length > 0 &&
    outcomeScoreTableData.rows.some((row) => row.outcome_scores.some((score) => score !== null))
  );
};
